// ProfessoresView — visão agregada dos professores: por company, com
// disciplinas, bancos e atividade recente. Filtros simples + KPI strip.

import { signal } from "@preact/signals";
import { useEffect, useMemo } from "preact/hooks";
import {
  AlertCircle,
  Building2,
  CalendarDays,
  Database,
  Library,
  Search,
  UserCog
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type ProfessorRow } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const professors = signal<ProfessorRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterCompany = signal<string>("all");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const res = await api.listProfessors();
    professors.value = res.professors;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function ProfessoresView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") return <ProfsLoading />;
  if (phase.value === "error") return <ProfsError />;
  return <ProfsReady />;
}

function ProfsLoading() {
  return (
    <div class="space-y-3">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-md" />
        ))}
      </div>
      <Skeleton className="h-12 rounded-md" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-md" />
      ))}
    </div>
  );
}

function ProfsError() {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Falha ao listar professores</h3>
          {errMsg.value && (
            <p class="text-xs text-muted-foreground">{errMsg.value}</p>
          )}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfsReady() {
  const rows = professors.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fc = filterCompany.value;

  const visible = useMemo(() => {
    return rows.filter((p) => {
      if (fc !== "all" && !p.companies.some((c) => c.companyId === fc))
        return false;
      if (q) {
        const hay = `${p.name} ${p.email} ${p.companies.map((c) => c.companyName).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fc]);

  const companies = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of rows) {
      for (const c of p.companies) {
        if (!map.has(c.companyId)) map.set(c.companyId, c.companyName);
      }
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "pt-BR")
    );
  }, [rows]);

  const kpis = useMemo(() => {
    const totalProfs = rows.length;
    const withDisciplina = rows.filter((p) => p.disciplinasCount > 0).length;
    const withBanco = rows.filter((p) => p.bancosCount > 0).length;
    const inativo30 = rows.filter((p) => {
      if (!p.lastSessionAt) return true;
      const days = (Date.now() - Date.parse(p.lastSessionAt)) / 86400000;
      return days >= 30;
    }).length;
    return { totalProfs, withDisciplina, withBanco, inativo30 };
  }, [rows]);

  return (
    <div class="space-y-4">
      {/* KPIs */}
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          icon={UserCog}
          label="Total"
          value={kpis.totalProfs}
          color="primary"
        />
        <Kpi
          icon={Library}
          label="Com disciplina"
          value={kpis.withDisciplina}
          color="emerald"
        />
        <Kpi
          icon={Database}
          label="Com banco próprio"
          value={kpis.withBanco}
          color="sky"
        />
        <Kpi
          icon={CalendarDays}
          label="Inativos 30d+"
          value={kpis.inativo30}
          color="amber"
        />
      </div>

      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Buscar nome, email, company…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <select
          value={filterCompany.value}
          onChange={(e) =>
            (filterCompany.value = (e.target as HTMLSelectElement).value)
          }
          class="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
        >
          <option value="all">Todas as companies</option>
          {companies.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum professor encontrado nesse filtro.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Professor</th>
                <th class="px-3 py-2">Companies</th>
                <th class="px-3 py-2 text-right tabular-nums">Disciplinas</th>
                <th class="px-3 py-2 text-right tabular-nums">Bancos</th>
                <th class="px-3 py-2">Última sessão</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <ProfRow key={p.id} prof={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: typeof UserCog;
  label: string;
  value: number;
  color: "primary" | "emerald" | "sky" | "amber";
}) {
  const cls =
    color === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : color === "sky"
        ? "bg-sky-500/10 text-sky-600 dark:text-sky-400"
        : color === "amber"
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
          : "bg-primary/10 text-primary";
  return (
    <div class={`rounded-md border border-border px-3 py-2.5 ${cls}`}>
      <div class="flex items-center gap-1.5">
        <Icon className="size-4" />
        <span class="text-[10px] font-bold uppercase tracking-wider opacity-80">
          {label}
        </span>
      </div>
      <div class="mt-1 text-2xl font-bold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function ProfRow({ prof }: { prof: ProfessorRow }) {
  return (
    <tr class="border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30">
      <td class="px-3 py-2">
        <div class="font-semibold">{prof.name}</div>
        <div class="font-mono text-[11px] text-muted-foreground">{prof.email}</div>
      </td>
      <td class="px-3 py-2">
        {prof.companies.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="space-y-0.5">
            {prof.companies.map((c) => (
              <div key={c.companyId} class="flex items-center gap-1 text-[11px]">
                <Building2 width="0.85em" height="0.85em" className="text-muted-foreground" />
                <span>{c.companyName}</span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td class="px-3 py-2 text-right tabular-nums font-bold">
        {prof.disciplinasCount}
      </td>
      <td class="px-3 py-2 text-right tabular-nums font-bold">
        {prof.bancosCount}
      </td>
      <td class="px-3 py-2 text-xs text-muted-foreground">
        {prof.lastSessionAt ? formatRel(prof.lastSessionAt) : "—"}
      </td>
    </tr>
  );
}

function formatRel(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  if (diff < 60_000) return "agora há pouco";
  if (diff < 3600_000) return `há ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `há ${Math.floor(diff / 3600_000)} h`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `há ${days} dia${days !== 1 ? "s" : ""}`;
  return new Date(iso).toLocaleDateString("pt-BR");
}
